// src/components/Auth/TurnstileWidget.jsx — SOUND-715
//
// Cloudflare Turnstile, wrapped so a form can (a) receive a token and (b) RESET
// the widget. Reset is not optional: a Turnstile token is single-use and
// expires after ~5 minutes, and the signup flow deliberately calls POST /users
// more than once — once before the person confirms their email and again after.
// Re-submitting a spent token gets `timeout-or-duplicate` back from Cloudflare
// and reads to the user as "we can't verify you're human" on a form they just
// completed.
//
// If REACT_APP_TURNSTILE_SITE_KEY is absent the component renders nothing and
// reports a null token. That mirrors the API, which skips verification when
// TURNSTILE_SECRET_KEY is unset — so an environment missing either half is
// simply un-gated rather than broken.

import React, {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

export const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
export const isTurnstileConfigured = () => Boolean(TURNSTILE_SITE_KEY);

// One shared script load for the whole app. Two widgets mounting at once must
// not append two <script> tags.
let scriptPromise = null;
function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        // Clear the cache so a later mount can retry rather than awaiting a
        // promise that will never settle.
        scriptPromise = null;
        reject(new Error("Turnstile script failed to load"));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

/**
 * @param {(token: string|null) => void} onToken  called with a token, or null
 *        when the token expires or the challenge errors
 * @param {() => void} [onError]
 * Exposes `reset()` via ref.
 */
const TurnstileWidget = forwardRef(function TurnstileWidget(
  { onToken, onError, className = "" },
  ref
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [failed, setFailed] = useState(false);

  // Keep the latest callbacks in a ref. Turnstile holds whatever function it
  // was rendered with, so closing over a stale `onToken` would silently deliver
  // tokens to a dead handler.
  const handlers = useRef({ onToken, onError });
  useEffect(() => {
    handlers.current = { onToken, onError };
  }, [onToken, onError]);

  useImperativeHandle(
    ref,
    () => ({
      reset() {
        if (window.turnstile && widgetIdRef.current !== null) {
          window.turnstile.reset(widgetIdRef.current);
          handlers.current.onToken?.(null);
        }
      },
    }),
    []
  );

  useEffect(() => {
    if (!isTurnstileConfigured()) {
      // Un-gated environment: report "no token" once so the form does not sit
      // waiting for one that is never coming.
      handlers.current.onToken?.(null);
      return undefined;
    }

    let cancelled = false;

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token) => handlers.current.onToken?.(token),
          // A token that expires while the person is still reading their inbox
          // must not stay in state — clear it so submit asks for a fresh one.
          "expired-callback": () => handlers.current.onToken?.(null),
          "error-callback": () => {
            handlers.current.onToken?.(null);
            handlers.current.onError?.();
          },
        });
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
        // Let the form proceed. The API fails open when Cloudflare is
        // unreachable, and email verification is still in the way.
        handlers.current.onToken?.(null);
        handlers.current.onError?.();
      });

    return () => {
      cancelled = true;
      if (window.turnstile && widgetIdRef.current !== null) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // Already gone.
        }
        widgetIdRef.current = null;
      }
    };
  }, []);

  if (!isTurnstileConfigured()) return null;

  return (
    <div className={className}>
      <div ref={containerRef} />
      {/* SOUND-726: this used to end "You can still continue." It is not true.
          AuthForm refuses to submit without a token, and the API refuses a
          missing one (SOUND-722), so someone reading that pressed Register and
          was told to wait a second — forever. The form renders the real
          explanation and the way out; this just stops contradicting it. */}
      {failed && (
        <p className="text-xs text-muted-foreground mt-1">
          Could not load the human-verification check.
        </p>
      )}
    </div>
  );
});

export default TurnstileWidget;
